/**
 * Cross-app addresses for the sibling SPAs that share this session cookie:
 * the admin center (web_admin) and the account hub (web_account).
 *
 * They are resolved from the CURRENT host at runtime, because the CRM is served
 * from a DIFFERENT base domain than its siblings: `crm.webadmin.ee` is the
 * WebAdmin-branded entry point, while the admin center and the account hub only
 * ever live on the Husly domain. Stripping the first host label the way
 * web_account does would yield `admin.webadmin.ee` / `account.webadmin.ee`, and
 * nginx serves neither (see deployment/husly/nginx-husly.conf: that domain has
 * only `crm.` and `portal.`), so a label swap would produce a dead link.
 */

/** Platform hosts - the fallback for every non-Husly CRM host. */
const PLATFORM_ADMIN_URL = "https://admin.husly.app";
const PLATFORM_ACCOUNT_URL = "https://account.husly.app";

function isLocalHost(hostname: string): boolean {
  return hostname === "localhost" || hostname.endsWith(".localhost") || hostname === "127.0.0.1";
}

/**
 * One sibling address.
 *   - dev (localhost:3313)  -> `devUrl`
 *   - *.husly.app           -> `<label>.<base>` (sibling host on the same domain)
 *   - anything else         -> `fallback` (crm.webadmin.ee lands here)
 * A `VITE_*` build arg overrides all of it (white-label deployments).
 */
function siblingUrl(opts: { override?: string; label: string; devUrl: string; fallback: string }): string {
  const override = opts.override?.trim();
  if (override) return override.replace(/\/+$/, "");
  if (typeof window === "undefined") return opts.devUrl;
  const { hostname, protocol } = window.location;
  if (isLocalHost(hostname)) return opts.devUrl;
  if (hostname === "husly.app" || hostname.endsWith(".husly.app")) {
    const base = hostname.split(".").slice(-2).join(".");
    return `${protocol}//${opts.label}.${base}`;
  }
  return opts.fallback;
}

/** web_admin SPA address - the "Ava halduskeskus" link (SSO on the same session cookie). */
export const ADMIN_URL = siblingUrl({
  override: import.meta.env.VITE_ADMIN_URL as string | undefined,
  label: "admin",
  devUrl: "http://localhost:3312",
  fallback: PLATFORM_ADMIN_URL,
});

/**
 * web_account SPA address - the "Ava konto" link. This is the self-service hub
 * (plan, credit wallet, usage meters, API keys, invoices) for the account that
 * OWNS this CRM, not a per-user profile page inside the CRM.
 */
export const ACCOUNT_URL = siblingUrl({
  override: import.meta.env.VITE_ACCOUNT_URL as string | undefined,
  label: "account",
  devUrl: "http://localhost:3314",
  fallback: PLATFORM_ACCOUNT_URL,
});
